import { Box, Chip, Divider, Skeleton, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { useGetAgencyInvoiceTransactions } from '@/apis/agency/billing/hook';
import { InvoiceTransactionResponse as AgencyInvoiceTransactionResponse } from '@/apis/agency/billing/types';
import { useGetBusinessInvoiceTransactions } from '@/apis/business/setting/billing/hook';
import { InvoiceTransactionResponse as BusinessInvoiceTransactionResponse } from '@/apis/business/setting/billing/types';
import { useBusiness } from '@/providers/context/BusinessProvider';
import { formatDollars, formatDate } from '@/utils/helperFunctions';
import linkCardLogo from '@/assets/LinkCardLogo.svg';
import { MdOutlineCreditCard } from 'react-icons/md';

type BillingHistoryRowDetailsProps = {
  invoiceId?: number;
};

const normalizeTransactions = (
  payload:
    | AgencyInvoiceTransactionResponse[]
    | BusinessInvoiceTransactionResponse[]
    | { data?: AgencyInvoiceTransactionResponse[] | BusinessInvoiceTransactionResponse[] }
    | undefined
) => {
  if (Array.isArray(payload)) return payload;
  if (payload?.data && Array.isArray(payload.data)) return payload.data;
  return [];
};

export default function BillingHistoryRowDetails({ invoiceId }: BillingHistoryRowDetailsProps) {
  const { selectedBusiness } = useBusiness();
  const isBusinessView = Boolean(selectedBusiness);

  const { data: businessInvoiceTransactions, isLoading: isBusinessTransactionsLoading } =
    useGetBusinessInvoiceTransactions(invoiceId, isBusinessView);

  const { data: agencyInvoiceTransactions, isLoading: isAgencyTransactionsLoading } = useGetAgencyInvoiceTransactions(
    invoiceId,
    isBusinessView
  );

  const invoiceTransactions = normalizeTransactions(
    isBusinessView ? businessInvoiceTransactions : agencyInvoiceTransactions
  );

  const isLoading = isBusinessView ? isBusinessTransactionsLoading : isAgencyTransactionsLoading;

  if (isLoading) {
    return <LoadingSkelton />;
  }

  if (!invoiceTransactions.length) {
    return (
      <TableRow sx={{ backgroundColor: '#F8F8F8', borderBottom: '1px solid #ECECEC' }}>
        <TableCell colSpan={7} sx={{ py: 2, textAlign: 'center', color: '#777777', fontSize: '1rem' }}>
          No Transaction
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow sx={{ backgroundColor: '#F8F8F8', borderBottom: '1px solid #ECECEC' }}>
      <TableCell colSpan={7} sx={{ py: { xs: 1.5, lg: 2 }, px: { xs: 2, lg: 2.5 } }}>
        <Stack divider={<Divider />} spacing={1}>
          {invoiceTransactions.map((transaction, index) => (
            <TransactionItem key={`${transaction.created}-${index}`} transaction={transaction} />
          ))}
        </Stack>
      </TableCell>
    </TableRow>
  );
}

type TransactionItemProps = {
  transaction: AgencyInvoiceTransactionResponse | BusinessInvoiceTransactionResponse;
};

const TransactionItem = ({ transaction }: TransactionItemProps) => {
  const isCard = transaction?.type === 'card';
  const status = transaction?.status || '';
  const statusLabel = status.charAt(0).toUpperCase() + status.slice(1);
  const isSucceeded = status === 'succeeded';
  const amount = (transaction?.amount ?? 0) / 100;
  const refundedAmount = (transaction?.amount_refunded ?? 0) / 100;
  const paymentMethodBrand = transaction?.brand?.toUpperCase() || 'CARD';
  const paymentMethodLast4 = transaction?.last4 || '----';
  const createdAt = transaction?.created ? formatDate(transaction.created * 1000, 'D MMM, HH:mm') : '-';

  return (
    <Box display='flex' alignItems='start' justifyContent='space-between' flexWrap='wrap' gap={1.5}>
      <Stack direction='column'>
        <Box display='flex' alignItems='center' gap={1} flexWrap='wrap'>
          <Typography fontSize={14}>US {formatDollars(amount)}</Typography>
          <Chip
            label={statusLabel}
            size='small'
            sx={{
              bgcolor: isSucceeded ? '#D6F5DE' : '#ECECEC',
              color: isSucceeded ? '#2E7D32' : '#777777',
              fontSize: 14,
              borderRadius: 6
            }}
          />
          {refundedAmount > 0 && (
            <Chip
              label={`Refund: ${formatDollars(refundedAmount)}`}
              size='small'
              sx={{ bgcolor: '#FFF3DF', color: '#B36B00', fontSize: 14, borderRadius: 6 }}
            />
          )}
        </Box>
        <Typography fontSize={14} color='text.secondary' sx={{ ml: 0.5 }}>
          {createdAt}
        </Typography>
      </Stack>
      <Box display='flex' alignItems='center' color='text.secondary' gap={0.5}>
        {isCard ? <MdOutlineCreditCard size={12} /> : <img src={linkCardLogo} />}
        <Typography color='text.secondary' fontSize={14}>
          {isCard ? `${paymentMethodBrand} •••• ${paymentMethodLast4}` : 'Link'}
        </Typography>
      </Box>
    </Box>
  );
};

const LoadingSkelton = () => {
  return (
    <TableRow sx={{ backgroundColor: '#F8F8F8', borderBottom: '1px solid #ECECEC' }}>
      <TableCell colSpan={7} sx={{ py: { xs: 1.5, lg: 2 }, px: { xs: 2, lg: 2.5 } }}>
        <Stack divider={<Divider />} spacing={1}>
          <Box display='flex' alignItems='start' justifyContent='space-between' flexWrap='wrap' gap={1.5}>
            {/* Left Section */}
            <Stack direction='column' spacing={0.5}>
              <Box display='flex' alignItems='center' gap={1}>
                <Skeleton variant='text' width={80} height={20} />
                <Skeleton variant='rounded' width={70} height={24} sx={{ borderRadius: 6 }} />
                <Skeleton variant='rounded' width={90} height={24} sx={{ borderRadius: 6 }} />
              </Box>

              <Skeleton variant='text' width={120} height={18} />
            </Stack>

            {/* Right Section */}
            <Box display='flex' alignItems='center' gap={0.5}>
              <Skeleton variant='circular' width={14} height={14} />
              <Skeleton variant='text' width={120} height={20} />
            </Box>
          </Box>
        </Stack>
      </TableCell>
    </TableRow>
  );
};
